import { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import useStore from './config/store';
import PsycholaborLayout from './maker/PsycholaborLayout';
import CapabilitiesLayout from './capabilities/CapabilitesLayout';
import PositionsLayout from './positions/PositionsLayout';
import ApplicantLayout from './applicants/ApplicantLayout';
import EvaluationLayout from './evaluations/EvaluationLayout';
import Assessment2Layout from './assessment2/Assessment2Layout'; // Assessment 2 (PLAN_ASSESSMENT2.md)
import ConfigurationLayout from './configuration/ConfigurationLayout';
import DashboardLayout from './dashboard/DashboardLayout';
import ComparisonLayout from './comparisons/ComparisonLayout';
import HomeLayout from './home/HomeLayout';
import DiscLayout from './disc/DiscLayout';
import ReasoningTestLayout from './reasoningTests/ReasoningTestLayout';
import PersonalHabilitiesTestLayout from './personalHabilitiesTests/PersonalHabilitiesTestLayout';
import CvFilteringLayout from './cvFiltering/CvFilteringLayout';
import MassiveRecruitmentLayout from './massiveRecruitment/MassiveRecruitmentLayout';
import SalesAssessmentLayout from './salesAssessment/SalesAssessmentLayout';
import AtsHomeLayout from './ats/home/AtsHomeLayout';
import AtsConfigurationLayout from './ats/configuration/AtsConfigurationLayout';
import AtsPositionsLayout from './ats/positions/AtsPositionsLayout';
import AtsPositionDetailLayout from './ats/positions/AtsPositionDetailLayout';
import AtsCandidatesLayout from './ats/candidates/AtsCandidatesLayout';
import AtsFairsLayout from './ats/fairs/AtsFairsLayout';
import AtsFairEditor from './ats/fairs/AtsFairEditor';
import HuntingHomeLayout from './hunting/HuntingHomeLayout';
import HuntingSearchLayout from './hunting/HuntingSearchLayout';
import AtsPositionWizard from './ats/positions/AtsPositionWizard';
import CaptationSimulator from './captation/CaptationSimulator';
import ErpHomeLayout from './erp/home/ErpHomeLayout';
import ErpClientsLayout from './erp/clients/ErpClientsLayout';
import ErpClientWizard from './erp/clients/ErpClientWizard';
import ErpClientDetailLayout from './erp/clients/ErpClientDetailLayout';
import ErpJobTitlesLayout from './erp/jobTitles/ErpJobTitlesLayout';
import ErpQuotationsLayout from './erp/quotations/ErpQuotationsLayout';
import ErpQuotationWizard from './erp/quotations/ErpQuotationWizard';
import ErpQuotationDetailLayout from './erp/quotations/ErpQuotationDetailLayout';
import ErpConfigurationLayout from './erp/configuration/ErpConfigurationLayout';
import ErpEmployeesLayout from './erp/employees/ErpEmployeesLayout';
import ErpEmployeeWizard from './erp/employees/ErpEmployeeWizard';
import ErpEmployeeDetailLayout from './erp/employees/ErpEmployeeDetailLayout';
import ErpContractsLayout from './erp/contracts/ErpContractsLayout';
import ErpContractDetailLayout from './erp/contracts/ErpContractDetailLayout';
import ErpExportLayout from './erp/export/ErpExportLayout';
import { canSeeErpSection } from './erp/erpAccess';

// Redirige desde "/" según organización/proyecto
const RedirectFromRoot = () => {
  const navigate = useNavigate();
  const { organization, project, appMode, user, erpRole, atsEnabled, huntingEnabled } = useStore();

  useEffect(() => {
    if (!organization) {
      navigate('/organizations');
    } else if (!project) {
      navigate(`/myOrganization`);
    } else {
      const goErp = appMode === 'erp' && (user?.rol === 'super-admin' || Boolean(erpRole));
      // ATS y Hunting: módulos de la organización (2026-10-26). Mientras no se
      // sabe (null) se respeta el modo guardado; el TopBar lo corrige al consultar.
      const goAts = appMode === 'ats' && atsEnabled !== false;
      const goHunting = appMode === 'hunting' && huntingEnabled !== false;
      navigate(goAts ? '/ats' : goHunting ? '/hunting' : goErp ? '/erp' : '/home');
    }
  }, [organization, project, appMode, user, navigate, atsEnabled, huntingEnabled]);

  return null;
};

// Componente para proteger rutas basado en el plan
const ProtectedRoute = ({ children, allowedForFree = false, allowedForTrial = false }) => {
  const { plan } = useStore();

  // Si el plan es free y la ruta no está permitida, redirigir al home
  if (plan === 'free' && !allowedForFree) {
    return <Navigate to="/home" replace />;
  }

  // Si el plan es trial y la ruta no está permitida, redirigir al home
  if (plan === 'trial' && !allowedForTrial) {
    return <Navigate to="/home" replace />;
  }

  return children;
};

const AppRoutes = () => {
  const { appMode, user, erpRole, atsEnabled, huntingEnabled } = useStore();
  const location = useLocation();

  // Guard de modos (Erwin, 2026-09-08): cada ruta pertenece a un modo
  // (/erp/* → erp, /ats/* → ats, /hunting/* → hunting, el resto → evaluaciones) y la navegación
  // OBEDECE al toggle seleccionado — una URL de otro modo rebota al home del
  // modo activo. Con esto un usuario "solo ERP" (sin toggle) queda encerrado
  // en /erp aunque escriba rutas a mano. Las rutas neutras (login, admin,
  // selección de organización/proyecto, públicas) viven fuera de AppRoutes
  // y no pasan por aquí. "/" queda exento: lo resuelve RedirectFromRoot.
  const pathMode = location.pathname.startsWith('/erp')
    ? 'erp'
    : location.pathname.startsWith('/ats')
      ? 'ats'
      : location.pathname.startsWith('/hunting')
        ? 'hunting'
        : 'legacy';
  const canUseErp = user?.rol === 'super-admin' || Boolean(erpRole);
  // ATS y Hunting: por organización, cualquier rol. Desde el 2026-10-26 son
  // módulos de la organización que enciende un super-admin (antes, listas
  // fijas en TopBar.js). Solo se saca a alguien del modo cuando se sabe que
  // está apagado: null = recién elegida la organización, todavía sin consultar.
  const canUseAts = atsEnabled !== false;
  const canUseHunting = huntingEnabled !== false;
  const effectiveMode = (appMode === 'erp' && !canUseErp)
    || (appMode === 'hunting' && !canUseHunting)
    || (appMode === 'ats' && !canUseAts)
    ? 'legacy'
    : appMode;

  if (location.pathname !== '/' && pathMode !== effectiveMode) {
    return (
      <Navigate
        to={effectiveMode === 'erp' ? '/erp' : effectiveMode === 'ats' ? '/ats' : effectiveMode === 'hunting' ? '/hunting' : '/home'}
        replace
      />
    );
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<RedirectFromRoot />} />

        <Route path="/home" element={<HomeLayout />} />

        {/* Rutas permitidas para plan free y trial */}
        <Route path="/configuration" element={<ConfigurationLayout />} />
        <Route path="/positions" element={<PositionsLayout />} />
        <Route path="/applicants" element={<ApplicantLayout />} />
        <Route path="/reports" element={<PsycholaborLayout />} />
        <Route path="/reports/:reportId" element={<PsycholaborLayout />} />

        {/* Rutas restringidas para plan free — permitidas para trial */}
        <Route
          path="/disc"
          element={
            <ProtectedRoute allowedForFree={false} allowedForTrial={true}>
              <DiscLayout />
            </ProtectedRoute>
          }
        />

        <Route
          path="/reasoning-tests"
          element={
            <ProtectedRoute allowedForFree={false} allowedForTrial={true}>
              <ReasoningTestLayout />
            </ProtectedRoute>
          }
        />

        <Route
          path="/cv_filtering"
          element={
            <ProtectedRoute allowedForFree={false} allowedForTrial={true}>
              <CvFilteringLayout />
            </ProtectedRoute>
          }
        />

        {/* Rutas restringidas para plan free Y trial */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowedForFree={false} allowedForTrial={false}>
              <DashboardLayout />
            </ProtectedRoute>
          }
        />

        <Route
          path="/personal-habilities"
          element={
            <ProtectedRoute allowedForFree={false} allowedForTrial={false}>
              <PersonalHabilitiesTestLayout />
            </ProtectedRoute>
          }
        />

        <Route
          path="/capabilities"
          element={
            <ProtectedRoute allowedForFree={false} allowedForTrial={false}>
              <CapabilitiesLayout />
            </ProtectedRoute>
          }
        />

        <Route
          path="/evaluations"
          element={
            <ProtectedRoute allowedForFree={false} allowedForTrial={false}>
              <EvaluationLayout />
            </ProtectedRoute>
          }
        />

        {/* Assessment 2 (PLAN_ASSESSMENT2.md): gate super-admin + organización dentro del layout */}
        <Route
          path="/assessment2"
          element={
            <ProtectedRoute allowedForFree={false} allowedForTrial={false}>
              <Assessment2Layout />
            </ProtectedRoute>
          }
        />

        <Route
          path="/report_comparison"
          element={
            <ProtectedRoute allowedForFree={false} allowedForTrial={false}>
              <ComparisonLayout />
            </ProtectedRoute>
          }
        />

        <Route
          path="/sales-assessment"
          element={
            <ProtectedRoute allowedForFree={false} allowedForTrial={false}>
              <SalesAssessmentLayout />
            </ProtectedRoute>
          }
        />

        <Route
          path="/massive-recruitment"
          element={
            <ProtectedRoute allowedForFree={false} allowedForTrial={false}>
              <MassiveRecruitmentLayout />
            </ProtectedRoute>
          }
        />

        {/** Opcional: si aún quieres que `/maker` funcione como alias */}
        <Route path="/maker" element={<Navigate to="/reports" replace />} />

        {/* ── RUTAS ATS ── */}
        {appMode === 'ats' && canUseAts && (
          <>
            <Route path="/ats" element={<AtsHomeLayout />} />
            <Route path="/ats/positions" element={<AtsPositionsLayout />} />
            <Route path="/ats/positions/new" element={<AtsPositionWizard />} />
            <Route path="/ats/positions/:positionId/edit" element={<AtsPositionWizard />} />
            <Route path="/ats/positions/:positionId" element={<AtsPositionDetailLayout />} />
            <Route path="/ats/positions/:positionId/simulator" element={<CaptationSimulator />} />
            <Route path="/ats/positions/:positionId/duplicate" element={<AtsPositionWizard />} />
            <Route path="/ats/candidates" element={<AtsCandidatesLayout />} />
            <Route path="/ats/configuration" element={<AtsConfigurationLayout />} />
            {/* Ferias: agrupaciones de vacantes con página pública y QR (PLAN_FERIAS) */}
            <Route path="/ats/fairs" element={<AtsFairsLayout />} />
            <Route path="/ats/fairs/new" element={<AtsFairEditor />} />
            <Route path="/ats/fairs/:fairId" element={<AtsFairEditor />} />
          </>
        )}

        {/* ── RUTAS HUNTING (búsqueda proactiva en Apollo — PLAN_HUNTING_APOLLO.md) ── */}
        {appMode === 'hunting' && canUseHunting && (
          <>
            <Route path="/hunting" element={<HuntingHomeLayout />} />
            <Route path="/hunting/search" element={<HuntingSearchLayout />} />
          </>
        )}

        {/* ── RUTAS ERP ──
            Roles v2 (matriz de accesos, erpAccess.js): cada grupo de rutas
            se monta solo si el rol ve la sección; una URL de una sección
            no permitida cae en "*" → "/" → home del modo. */}
        {appMode === 'erp' && (user?.rol === 'super-admin' || Boolean(erpRole)) && (
          <>
            <Route path="/erp" element={<ErpHomeLayout />} />
            {canSeeErpSection(user, erpRole, 'clients') && (
              <>
                <Route path="/erp/clients" element={<ErpClientsLayout />} />
                <Route path="/erp/clients/new" element={<ErpClientWizard />} />
                <Route path="/erp/clients/:clientId" element={<ErpClientDetailLayout />} />
              </>
            )}
            {canSeeErpSection(user, erpRole, 'quotations') && (
              <>
                <Route path="/erp/quotations" element={<ErpQuotationsLayout />} />
                <Route path="/erp/quotations/new" element={<ErpQuotationWizard key="new" />} />
                <Route path="/erp/quotations/:quotationId/edit" element={<ErpQuotationWizard key="edit" />} />
                <Route path="/erp/quotations/:quotationId" element={<ErpQuotationDetailLayout />} />
              </>
            )}
            {canSeeErpSection(user, erpRole, 'employees') && (
              <>
                <Route path="/erp/employees" element={<ErpEmployeesLayout />} />
                <Route path="/erp/employees/new" element={<ErpEmployeeWizard />} />
                <Route path="/erp/employees/:employeeRecordId" element={<ErpEmployeeDetailLayout />} />
              </>
            )}
            {canSeeErpSection(user, erpRole, 'contracts') && (
              <>
                <Route path="/erp/contracts" element={<ErpContractsLayout />} />
                <Route path="/erp/contracts/:contractId" element={<ErpContractDetailLayout />} />
              </>
            )}
            {canSeeErpSection(user, erpRole, 'export') && (
              <Route path="/erp/export" element={<ErpExportLayout />} />
            )}
            {canSeeErpSection(user, erpRole, 'jobTitles') && (
              <Route path="/erp/job-titles" element={<ErpJobTitlesLayout />} />
            )}
            {canSeeErpSection(user, erpRole, 'configuration') && (
              <Route path="/erp/configuration" element={<ErpConfigurationLayout />} />
            )}
          </>
        )}

        {/** Todo lo demás al root */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

export default AppRoutes;